import { useEffect, useRef, useState, FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';

const WHATSAPP_URL = 'https://wa.me/5516981106759?text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20jur%C3%ADdica';
const FORMSPREE_URL = 'https://formspree.io/f/xnjolley';

const contactInfo = [
  { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: '+55 (16) 98110-6759', href: WHATSAPP_URL, highlight: true },
  { icon: 'ri-phone-line', label: 'Telefone', value: '+55 (16) 98110-6759', href: 'tel:+5516981106759', highlight: false },
  { icon: 'ri-mail-line', label: 'E-mail', value: 'lcadvogadosbr@gmail.com', href: 'mailto:lcadvogadosbr@gmail.com', highlight: false },
  { icon: 'ri-map-pin-line', label: 'Endereço', value: 'Av. Paulo VI, 1986 – Residencial Paraíso, Franca/SP', href: 'https://maps.google.com/?q=Avenida+Paulo+VI,1986,Residencial+Paraíso,Franca,SP', highlight: false },
  { icon: 'ri-instagram-line', label: 'Instagram', value: '@lc.advogadosbr', href: 'https://instagram.com/lc.advogadosbr', highlight: false },
];

export default function Contact() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    const form = e.currentTarget;
    const formData = new FormData(form);
    const nome = formData.get('nome') as string;
    const email = formData.get('email') as string;
    const telefone_whatsapp = formData.get('telefone') as string;
    const mensagem = formData.get('mensagem') as string;

    setStatus('sending');
    try {
      // Save to Supabase (primary)
      const { error: supabaseError } = await supabase
        .from('contatos')
        .insert({ nome, email, telefone_whatsapp, mensagem });

      if (supabaseError) {
        console.error('Supabase error:', supabaseError);
        setStatus('error');
        return;
      }

      // Also send via Formspree as backup notification
      try {
        const data = new URLSearchParams(formData as unknown as URLSearchParams).toString();
        await fetch(FORMSPREE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
          body: data,
        });
      } catch {
        // Formspree failure is non-blocking — Supabase already saved
      }

      setStatus('success');
      form.reset();
      setCharCount(0);
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contato" className="py-28 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        <div
          className="text-center mb-14"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <span className="text-xs font-bold tracking-widest uppercase mb-3 inline-block" style={{ color: '#C9A24A' }}>
            Fale conosco
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#0B2F5B' }}>
            Entre em <span style={{ color: '#C9A24A' }}>Contato</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#1A1A1A', opacity: 0.65 }}>
            Estamos prontos para ouvir seu caso e oferecer o suporte jurídico que você precisa.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Contact info */}
          <div
            className="lg:col-span-2 flex flex-col gap-4"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s',
            }}
          >
            {contactInfo.map((c, i) => (
              <a
                key={i}
                href={c.href}
                target={c.href.startsWith('http') ? '_blank' : undefined}
                rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="flex items-center gap-4 rounded-xl px-5 py-4 cursor-pointer transition-all duration-200 hover:scale-102 group"
                style={{
                  background: c.highlight ? '#0B2F5B' : 'rgba(11,47,91,0.04)',
                  border: `1px solid ${c.highlight ? '#0B2F5B' : 'rgba(11,47,91,0.1)'}`,
                  textDecoration: 'none',
                }}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center rounded-full shrink-0"
                  style={{ background: c.highlight ? 'rgba(201,162,74,0.2)' : 'linear-gradient(135deg,#C9A24A,#E1C67A)' }}
                >
                  <i className={`${c.icon} text-lg`} style={{ color: c.highlight ? '#C9A24A' : '#0B2F5B' }} />
                </div>
                <div>
                  <p className="text-xs font-semibold mb-0.5" style={{ color: c.highlight ? 'rgba(255,255,255,0.6)' : '#1A1A1A', opacity: 0.6 }}>{c.label}</p>
                  <p className="text-sm font-semibold" style={{ color: c.highlight ? '#FFFFFF' : '#0B2F5B' }}>{c.value}</p>
                </div>
              </a>
            ))}

            {/* Map */}
            <div className="rounded-xl overflow-hidden mt-2" style={{ height: '240px' }}>
              <iframe
                title="Localização LC Advogados – Franca SP"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3726.4867374085257!2d-47.41036502479717!3d-20.538638580718394!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94b0b8b2e7a90001%3A0x0!2sAv.+Paulo+VI%2C+1986+-+Residencial+Para%C3%ADso%2C+Franca+-+SP!5e0!3m2!1spt-BR!2sbr!4v1711000000000"
                width="100%"
                height="240"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div
            className="lg:col-span-3"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'all 0.7s cubic-bezier(0.4,0,0.2,1) 0.15s',
            }}
          >
            <div className="rounded-2xl p-8 md:p-10" style={{ background: '#F8F9FD', border: '1px solid rgba(11,47,91,0.08)' }}>
              <h3 className="font-serif font-bold text-2xl mb-2" style={{ color: '#0B2F5B' }}>Envie sua Mensagem</h3>
              <p className="text-sm mb-8" style={{ color: '#1A1A1A', opacity: 0.55 }}>Preencha o formulário e entraremos em contato em breve.</p>

              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg,#C9A24A,#E1C67A)' }}>
                    <i className="ri-check-line text-2xl" style={{ color: '#0B2F5B' }} />
                  </div>
                  <h4 className="font-bold text-xl mb-2" style={{ color: '#0B2F5B' }}>Mensagem enviada!</h4>
                  <p className="text-sm" style={{ color: '#1A1A1A', opacity: 0.65 }}>Entraremos em contato em breve. Você também pode nos chamar no WhatsApp.</p>
                  <button onClick={() => setStatus('idle')} className="mt-6 text-sm font-semibold cursor-pointer" style={{ color: '#C9A24A' }}>Enviar outra mensagem</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form action={FORMSPREE_URL} method="POST" id="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: '#0B2F5B' }}>Nome completo *</label>
                      <input
                        type="text"
                        name="nome"
                        required
                        placeholder="Seu nome"
                        className="w-full h-14 px-4 rounded-xl text-sm outline-none transition-all duration-200"
                        style={{ background: '#fff', border: '2px solid rgba(11,47,91,0.15)', color: '#1A1A1A' }}
                        onFocus={(e) => { e.target.style.borderColor = '#C9A24A'; e.target.style.boxShadow = '0 0 0 3px rgba(201,162,74,0.12)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(11,47,91,0.15)'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: '#0B2F5B' }}>E-mail *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="seu@email.com"
                        className="w-full h-14 px-4 rounded-xl text-sm outline-none transition-all duration-200"
                        style={{ background: '#fff', border: '2px solid rgba(11,47,91,0.15)', color: '#1A1A1A' }}
                        onFocus={(e) => { e.target.style.borderColor = '#C9A24A'; e.target.style.boxShadow = '0 0 0 3px rgba(201,162,74,0.12)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(11,47,91,0.15)'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold mb-2" style={{ color: '#0B2F5B' }}>Telefone / WhatsApp *</label>
                      <input
                        type="tel"
                        name="telefone"
                        required
                        placeholder="(00) 00000-0000"
                        className="w-full h-14 px-4 rounded-xl text-sm outline-none transition-all duration-200"
                        style={{ background: '#fff', border: '2px solid rgba(11,47,91,0.15)', color: '#1A1A1A' }}
                        onFocus={(e) => { e.target.style.borderColor = '#C9A24A'; e.target.style.boxShadow = '0 0 0 3px rgba(201,162,74,0.12)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(11,47,91,0.15)'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold mb-2" style={{ color: '#0B2F5B' }}>Descreva seu caso *</label>
                    <textarea
                      name="mensagem"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Descreva brevemente o que aconteceu..."
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none transition-all duration-200"
                      style={{ background: '#fff', border: '2px solid rgba(11,47,91,0.15)', color: '#1A1A1A' }}
                      onFocus={(e) => { e.target.style.borderColor = '#C9A24A'; e.target.style.boxShadow = '0 0 0 3px rgba(201,162,74,0.12)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(11,47,91,0.15)'; e.target.style.boxShadow = 'none'; }}
                      onChange={(e) => setCharCount(e.target.value.length)}
                    />
                    <p className="text-xs mt-1.5 text-right" style={{ color: charCount > 500 ? '#e74c3c' : '#1A1A1A', opacity: 0.5 }}>{charCount}/500</p>
                  </div>

                  {status === 'error' && (
                    <p className="text-sm mb-4 px-4 py-3 rounded-lg" style={{ background: 'rgba(231,76,60,0.08)', color: '#e74c3c' }}>
                      Ocorreu um erro. Tente novamente ou fale via WhatsApp.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || charCount > 500}
                    className="w-full h-14 rounded-xl font-bold text-base flex items-center justify-center gap-3 cursor-pointer whitespace-nowrap transition-all duration-300 hover:opacity-90 disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg,#0B2F5B,#123E73)', color: '#FFFFFF' }}
                  >
                    {status === 'sending' ? (
                      <><i className="ri-loader-4-line animate-spin" /> Enviando...</>
                    ) : (
                      <><i className="ri-send-plane-2-line" /> Enviar mensagem</>
                    )}
                  </button>

                  <div className="mt-6 pt-6 border-t" style={{ borderColor: 'rgba(11,47,91,0.1)' }}>
                    <p className="text-sm text-center mb-3" style={{ color: '#1A1A1A', opacity: 0.5 }}>ou prefere resposta imediata?</p>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-all duration-300 hover:opacity-90"
                      style={{ background: '#25D366', color: '#FFFFFF' }}
                    >
                      <i className="ri-whatsapp-line text-base" />
                      Chamar no WhatsApp
                    </a>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
